import type { MutableRefObject } from 'react';

import { notifyBugsnag } from 'utils/bugsnag';

import { didUpdatePreferencePayload } from './didUpdatePreference';

type PreferenceMutationResult = {
  data?: unknown;
  error?: unknown;
  errors?: unknown;
};

export type RunPreferenceMutationParams<TResult extends PreferenceMutationResult> = {
  isUpdatingRef: MutableRefObject<boolean>;
  mutate: () => Promise<TResult>;
  getPayload: (result: TResult) => unknown;
  message: {
    success: (content: string, duration?: number) => void;
    error: (content: string) => void;
  };
  successMessage: string;
  errorMessage: string;
  successDuration?: number;
  onSuccess?: () => void;
  resetGuardOnSuccess?: boolean;
  silent?: boolean;
};

/**
 * Guard + validate + toast for preference mutations. Apollo often resolves on
 * GraphQL errors, so callers must pass a payload selector rather than treating
 * a settled promise as success.
 */
export const runPreferenceMutation = async <TResult extends PreferenceMutationResult>({
  isUpdatingRef,
  mutate,
  getPayload,
  message,
  successMessage,
  errorMessage,
  successDuration,
  onSuccess,
  resetGuardOnSuccess,
  silent,
}: RunPreferenceMutationParams<TResult>): Promise<boolean> => {
  if (isUpdatingRef.current) {
    return false;
  }

  isUpdatingRef.current = true;
  let succeeded = false;

  try {
    const result = await mutate();
    if (!didUpdatePreferencePayload(result, getPayload(result))) {
      throw new Error('Preference mutation returned no payload');
    }
    succeeded = true;
  } catch (error) {
    notifyBugsnag(error instanceof Error ? error : new Error(String(error)));
    message.error(errorMessage);
    return false;
  } finally {
    if (!succeeded || resetGuardOnSuccess !== false) {
      isUpdatingRef.current = false;
    }
  }

  try {
    onSuccess?.();
  } catch {
    isUpdatingRef.current = false;
  }

  if (!silent) {
    if (successDuration === undefined) {
      message.success(successMessage);
    } else {
      message.success(successMessage, successDuration);
    }
  }
  return true;
};
